import React, { useState, useEffect } from 'react';
import { Box, TextField, Typography, Alert, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { validatePassword } from '../../helpers/validation';
import { goToSinginUrl } from '../../lib/routingUtils';
import { resetPassword } from './authThunks';
import { useQuery } from '../../hooks/urlHooks';
import { RootState } from '../../reducers';

const ResetPassword = () => {
  const { token: tokenFromUrl } = useQuery();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { status } = useSelector((state: RootState) => ({
    status: state.auth.resetPassword.status,
  }));
  const [newPassword, setNewPassword] = useState({
    value: '',
    isError: false,
  });
  const [confirmPassword, setConfirmPassword] = useState({
    value: '',
    isError: false,
  });
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [tokenFromUrl]);

  const handleNewPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newPasswordValue = event.target.value;
    const isValid = !validatePassword(newPasswordValue);
    setNewPassword({ value: newPasswordValue, isError: !isValid });

    const passwordsMatch = newPasswordValue === confirmPassword.value;
    setConfirmPassword({
      value: confirmPassword.value,
      isError: !passwordsMatch,
    });
  };

  const handleConfirmPasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const confirmPasswordValue = event.target.value;
    const passwordsMatch = newPassword.value === confirmPasswordValue;
    setConfirmPassword({
      value: confirmPasswordValue,
      isError: !passwordsMatch,
    });
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    dispatch(
      resetPassword({
        token,
        password: newPassword.value,
        confirm: confirmPassword.value,
      })
    );
    setTimeout(() => {
      goToSinginUrl();
    }, 3000);
  };

  const renderResponseMessage = () => {
    if (status === 'failed') {
      return (
        <Alert severity="error" id="reset_password_error_alert" sx={{ mt: 2 }}>
          An error occurred. Please correct the issues and try again.
        </Alert>
      );
    }
    if (status === 'succeeded') {
      return (
        <Alert
          severity="success"
          id="reset_password_success_alert"
          sx={{ mt: 2 }}
        >
          Password reset successful. Please check your inbox.
        </Alert>
      );
    }
    return null;
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      padding={2}
    >
      <Typography variant="h4" gutterBottom>
        Reset Password
      </Typography>
      <TextField
        label="New Password"
        variant="outlined"
        fullWidth
        margin="normal"
        type="password"
        value={newPassword.value}
        onChange={handleNewPasswordChange}
        error={newPassword.isError}
        helperText={
          newPassword.isError ? 'Password does not meet the criteria.' : ''
        }
        sx={{ mb: 2, width: 320 }}
      />
      <TextField
        label="Confirm Password"
        variant="outlined"
        fullWidth
        margin="normal"
        type="password"
        value={confirmPassword.value}
        onChange={handleConfirmPasswordChange}
        error={confirmPassword.isError}
        helperText={confirmPassword.isError ? 'Passwords do not match.' : ''}
        sx={{ mb: 2, width: 320 }}
      />
      <LoadingButton
        id="reset_password_button"
        variant="contained"
        tabIndex={-1}
        loading={status === 'pending'}
        loadingPosition="start"
        type="submit"
        disabled={
          !newPassword.value ||
          newPassword.isError ||
          !confirmPassword.value ||
          confirmPassword.isError
        }
        fullWidth
        sx={{ padding: theme.spacing(2), width: 320 }}
      >
        Reset Password
      </LoadingButton>
      {renderResponseMessage()}
    </Box>
  );
};

export default ResetPassword;
